import { useState } from 'react';
import { Envelope, InstagramLogo, ArrowRight } from 'phosphor-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../api/client';
import { API_ENDPOINTS } from '../../api/config';
import { trackWaitlistJoin, trackSignUp } from '@/lib/analytics';

const INSTAGRAM_URL = 'https://instagram.com/paladinoai.br';

export function WaitlistForm() {
  const [step, setStep] = useState<'follow' | 'email'>('follow');
  const [hasOpenedInstagram, setHasOpenedInstagram] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');

    try {
      // Use the part before @ as the name if not provided
      const name = email.split('@')[0];

      await apiClient.post(API_ENDPOINTS.WAITLIST, {
        email,
        name,
      });

      trackWaitlistJoin();
      trackSignUp('waitlist');
      setStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      setStatus('error');
      // Reset to idle after 3 seconds on error so user can try again
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center p-6 text-center border border-[color:var(--brand-500)]/20 rounded-xl bg-[color:var(--brand-500)]/5"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-[color:var(--brand-500)] to-indigo-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-[color:var(--brand-500)]/20">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold theme-text mb-2">Você está na lista!</h3>
            <p className="theme-text-muted text-sm">
              Seus 50 créditos do bundle starter estão garantidos. Avisaremos assim que seu acesso for liberado.
            </p>
          </motion.div>
        ) : step === 'follow' ? (
          <motion.div
            key="follow"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="w-full bg-[color:var(--card-bg)] border theme-border rounded-xl p-6 md:p-8 shadow-sm text-center"
          >
            <p className="text-sm theme-text-muted mb-6">
              Siga-nos no Instagram para continuar e garantir seus 50 créditos.
            </p>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setHasOpenedInstagram(true)}
              className="inline-flex items-center justify-center gap-2 w-full max-w-xs mx-auto h-12 px-4 rounded-xl border-2 border-[color:var(--nt-primary)]/40 bg-[color:var(--nt-primary)]/10 text-[color:var(--nt-primary)] font-semibold hover:bg-[color:var(--nt-primary)]/20 transition-colors"
            >
              <InstagramLogo className="w-6 h-6" weight="fill" />
              @paladinoai.br
            </a>
            {hasOpenedInstagram && (
              <>
                <p className="text-xs theme-text-muted mt-4 mb-6">
                  O link abre em uma nova guia. Depois de seguir, volte aqui e clique abaixo.
                </p>
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="inline-flex items-center justify-center gap-2 w-full h-10 px-4 rounded-lg text-sm font-semibold bg-[color:var(--nt-primary)] text-white hover:brightness-95 transition-all"
                >
                  Já segui, continuar
                  <ArrowRight className="w-4 h-4" weight="bold" />
                </button>
              </>
            )}
          </motion.div>
        ) : (
          <motion.form
            key="email"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            onSubmit={handleSubmit}
            className="w-full bg-[color:var(--card-bg)] border theme-border rounded-xl p-3 md:p-4 shadow-sm text-left"
          >
            <div className="space-y-2">
              <label htmlFor="waitlist-email" className="block text-xs font-medium theme-text ml-1">
                Email corporativo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Envelope className="w-5 h-5 text-[color:var(--foreground-muted)]/60" weight="regular" />
                </div>
                <input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@empresa.com"
                  className={`w-full pl-12 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-500)]/40 focus:border-[color:var(--brand-500)] transition-all duration-200 theme-input theme-text placeholder:text-[color:var(--foreground-muted)]/60 placeholder:text-sm text-sm ${status === 'error' ? 'border-red-500' : ''}`}
                  required
                />
              </div>
              {status === 'error' && (
                <p className="text-xs text-red-500 ml-1">Ocorreu um erro ao cadastrar. Tente novamente.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="mt-4 relative inline-flex items-center justify-center w-full h-10 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer disabled:pointer-events-none bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top),0_4px_12px_-2px_var(--nt-primary)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover),0_4px_12px_-2px_var(--nt-primary)] !gap-0 group"
              style={{ boxShadow: 'inset 0 -0.125rem 0 0 var(--nt-primary-inset-bottom), inset 0 0.125rem 0 0 var(--nt-primary-inset-top), 0 4px 12px -2px rgba(139, 92, 246, 0.25)' }}
            >
              <span className="absolute -top-2.5 -right-2 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-indigo-400 shadow-sm z-10 animate-bounce-slow">
                +50 créditos
              </span>

              {status === 'loading' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Entrando...</span>
                </span>
              ) : (
                'Entrar na Waitlist'
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
